    import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
    import { useGridFilter } from "ag-grid-react";

    const ITEMS_PER_LOAD = 100;

    const sortFilterValues = (values) =>
        [...values].sort((a, b) => {
            const strA = String(a).trim();
            const strB = String(b).trim();

            const group = (val) => {
                if (/^\d+$/.test(val)) return 1;
                if (/^\d/.test(val)) return 2;
                return 3;
            };

            const groupA = group(strA);
            const groupB = group(strB);

            if (groupA !== groupB) return groupA - groupB;

            return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" });
        });

    const CustomCheckboxFilter = ({ model, onModelChange, column, api, options, loadOptions, serverSide }) => {
        const inputRef = useRef(null);
        const scrollContainerRef = useRef(null);

        const [allOptions, setAllOptions] = useState([]);
        const [filteredOptions, setFilteredOptions] = useState([]);
        const [visibleOptions, setVisibleOptions] = useState([]);
        const [searchTerm, setSearchTerm] = useState("");
        const [isLoading, setIsLoading] = useState(false);

        // Load unique values once when filter opens.
        // loadOptions asks the server (bets listing). Otherwise scan loaded rows.
        useEffect(() => {
            if (typeof loadOptions === "function") return undefined;
            if (!api || !column?.colId) return undefined;

            setIsLoading(true);
            const uniqueValues = new Set();

            if (Array.isArray(options) && options.length > 0) {
                options.forEach((value) => {
                    if (value !== undefined && value !== null) uniqueValues.add(value.toString());
                });
            } else {
                api.forEachNodeAfterFilterAndSort((node) => {
                    const val = node.data?.[column.colId];
                    if (val !== undefined && val !== null) {
                        uniqueValues.add(val.toString());
                    }
                });
            }

            setAllOptions(sortFilterValues(uniqueValues));
            setSearchTerm("");
            setIsLoading(false);
            return undefined;
        }, [api, column, loadOptions, options]);

        useEffect(() => {
            if (typeof loadOptions !== "function") return undefined;
            let cancelled = false;
            const timer = setTimeout(async () => {
                setIsLoading(true);
                try {
                    const values = await loadOptions(searchTerm);
                    if (!cancelled) {
                        setAllOptions(sortFilterValues((values || []).map((value) => String(value))));
                    }
                } catch (error) {
                    console.error("Filter options failed", error);
                    if (!cancelled) setAllOptions([]);
                } finally {
                    if (!cancelled) setIsLoading(false);
                }
            }, 300);

            return () => {
                cancelled = true;
                clearTimeout(timer);
            };
        }, [loadOptions, searchTerm]);

        // Filter options based on search input
        useEffect(() => {
            const filtered = allOptions.filter((val) =>
                val.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredOptions(filtered);
            setVisibleOptions(filtered.slice(0, ITEMS_PER_LOAD));
        }, [searchTerm, allOptions]);

        // Infinite scroll loader
        const handleScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const nearBottom =
                container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

            if (nearBottom && visibleOptions.length < filteredOptions.length) {
                setVisibleOptions((prev) => [
                    ...prev,
                    ...filteredOptions.slice(prev.length, prev.length + ITEMS_PER_LOAD),
                ]);
            }
        };

        // AG Grid filtering logic
        const doesFilterPass = useCallback(
            (params) => {
                if (serverSide) return true;
                const val = params.data?.[column.colId];
                return (model?.values || []).includes(val?.toString());
            },
            [model, column, serverSide]
        );

        const afterGuiAttached = useCallback((params) => {
            if (!params?.suppressFocus) {
                inputRef.current?.focus();
            }
        }, []);

        useGridFilter({ doesFilterPass, afterGuiAttached });

        const handleSelectionChange = (checked, value) => {
            const current = new Set(model?.values || []);
            checked ? current.add(value) : current.delete(value);

            const updated = [...current];
            onModelChange(updated.length > 0 ? { values: updated } : null);
        };

        const handleSelectAll = () => {
            onModelChange({ values: filteredOptions });
        };

        const handleDeselectAll = () => {
            onModelChange(null);
        };

        return (
            <div className="custom-filter bg-white p-4 rounded-lg shadow-md w-72">
                <div className="mb-2">
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded"
                    />
                </div>

                <div className="flex gap-2 mb-3">
                    <button
                       className="text-xs text-white bg-blue-800 px-3 py-1 rounded hover:bg-blue-900"
                        onClick={handleSelectAll}
                    >
                        Select All
                    </button>
                    <button
                         className="text-xs text-white bg-gray-500 px-3 py-1 rounded hover:bg-gray-600"
                        onClick={handleDeselectAll}
                    >
                        Deselect All
                    </button>
                </div>

                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="max-h-48 overflow-y-auto pr-2 pt-2"
                >
                    {isLoading ? (
                        <p className="text-sm text-gray-500 italic px-2">Loading options...</p>
                    ) : visibleOptions.length === 0 ? (
                        <p className="text-sm text-gray-500 italic px-2">No values found</p>
                    ) : (
                        visibleOptions.map((value) => (
                            <div key={value} className="flex items-center mb-1 px-1">
                                <input
                                    type="checkbox"
                                    checked={(model?.values || []).includes(value)}
                                    onChange={(e) => handleSelectionChange(e.target.checked, value)}
                                    className="mr-2"
                                />
                                <span className="text-sm text-gray-700">{value}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    export default CustomCheckboxFilter;
